import {
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Link,
} from "@mui/material";
import { EPath } from "constants/path";
import React, { useMemo } from "react";
import { useNavigate } from "react-router";

import LegalDialog from "./LegalDialog";

interface DisclaimerConsentModalProps {
  open: boolean;
  onBack: () => void;
  onAgree: () => void;
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
  valuePrivacyChecked: boolean;
  valueTermsChecked: boolean;
  onChangePrivacy: (checked: boolean) => void;
  onChangeTerms: (checked: boolean) => void;
}

const DisclaimerConsentModal: React.FC<DisclaimerConsentModalProps> = ({
  open,
  onBack,
  onAgree,
  onOpenPrivacy,
  onOpenTerms,
  valuePrivacyChecked,
  valueTermsChecked,
  onChangePrivacy,
  onChangeTerms,
}) => {
  const navigate = useNavigate();
  const canAgree = useMemo(
    () => valuePrivacyChecked && valueTermsChecked,
    [valuePrivacyChecked, valueTermsChecked]
  );

  const handleClose = () => {
    navigate(EPath.LOGIN);
  };

  return (
    <LegalDialog
      open={open}
      onClose={handleClose}
      title="Your Data is Secure With Us. Always Secure."
      maxWidth="sm"
      contentSx={{
        "& .MuiTypography-root:not(.MuiLink-root)": { color: "#000" },
        "& .MuiLink-root": {
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 600,
        },
      }}
      actions={
        <>
          <Button
            onClick={onBack}
            variant="outlined"
            className="px-8 py-2"
            sx={{ borderRadius: "9999px", color: "#000", flex: 1 }}
            fullWidth
          >
            Go Back
          </Button>
          <Button
            onClick={onAgree}
            disabled={!canAgree}
            variant="contained"
            className="px-8 py-2"
            sx={{ borderRadius: "9999px", flex: 1 }}
            fullWidth
          >
            I Agree
          </Button>
        </>
      }
    >
      <div className="pt-2 space-y-3">
        <Typography variant="body2">
          Any information you give to us is covered in line with our Terms &
          Conditions and Privacy Policy. Please read them first.
        </Typography>

        <div className="space-y-2">
          <FormControlLabel
            sx={{ display: "flex", alignItems: "center", mt: 1 }}
            control={
              <Checkbox
                checked={valuePrivacyChecked}
                onChange={(e) => onChangePrivacy(e.target.checked)}
              />
            }
            label={
              <Typography
                component="span"
                variant="body2"
                sx={{ display: "inline" }}
              >
                I agree to the{" "}
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenPrivacy();
                  }}
                  underline="none"
                >
                  Privacy Policy
                </Link>
              </Typography>
            }
          />
          <FormControlLabel
            sx={{ display: "flex", alignItems: "center", mt: 1 }}
            control={
              <Checkbox
                checked={valueTermsChecked}
                onChange={(e) => onChangeTerms(e.target.checked)}
              />
            }
            label={
              <Typography
                component="span"
                variant="body2"
                sx={{ display: "inline" }}
              >
                I agree to the{" "}
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenTerms();
                  }}
                  underline="none"
                >
                  Terms & Conditions
                </Link>
              </Typography>
            }
          />
        </div>
      </div>
    </LegalDialog>
  );
};

export default DisclaimerConsentModal;
